<template>
  <div>
    <v-row
      v-intersect="{ handler: onIntersect }"
      no-gutters
      justify="center"
    >
      <v-col cols="14">
        <v-row
          align="center"
          no-gutters
          class="fill-height"
        >
          <v-col
            class="px-6 py-4"
            cols="auto"
          >
            <v-avatar
              color="grey lighten-4"
              size="110px"
            >
              <img
                v-if="yacht.model.main_image"
                :src="yacht.model.main_image.path"
                alt="Main image"
              >
            </v-avatar>
          </v-col>
          <v-col class="fill-height">
            <v-row
              no-gutters
              class="flex-column fill-height"
            >
              <v-row no-gutters>
                <v-col>
                  <div class="title mb-1">
                    <strong>#{{ yacht.id }}</strong> &middot;
                    <span v-if="yacht.name">
                      {{ yacht.name }}
                    </span>
                    <span
                      v-else
                      class="grey--text text--lighten-1"
                    >
                      No Name
                    </span>
                  </div>
                  <div class="body-2 mb-1">
                    Pricelist {{ yacht.production_year }} &middot;
                    {{ yacht.model.full_label }} <span class="grey--text">
                      #{{ yacht.model.id }}
                    </span> &middot;
                    Model {{ yacht.model.model_year }} &middot;
                    <flag
                      :iso="yacht.country.code"
                      :squared="false"
                    />
                    <span
                      v-if="yacht.destination_base || yacht.current_base"
                    >
                      {{ yacht.current_base ? yacht.current_base.name : yacht.destination_base.name }}
                    </span>
                    ({{ yacht.country.name }})
                  </div>
                  <div class="grey--text text--lighten-1 caption">
                    updated {{ yacht.updated_at | moment('from') }}
                  </div>
                </v-col>
                <v-col
                  v-if="yacht.is_original_different && yacht.investment_status !== yachtStatuses.INVESTMENT_STATUS_SOLD && $can('edit', 'Yacht')"
                  cols="auto"
                >
                  <v-chip
                    outlined
                    color="error"
                    @click="$emit('edit:yacht')"
                  >
                    Updates available - reconfigure
                  </v-chip>
                </v-col>
                <v-col cols="auto">
                  <!-- todo: set permission-->
                  <v-tooltip
                    v-if="$can('edit', 'Yacht')"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        class="mr-2"
                        v-on="on"
                        @click="$emit('edit:yacht')"
                      >
                        <v-icon>
                          edit
                        </v-icon>
                      </v-btn>
                    </template>
                    Edit yacht
                  </v-tooltip>
                  <v-tooltip
                    v-if="$can('create-documents', 'Yacht')"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        class="mr-2"
                        v-on="on"
                        @click="$emit('document:select-type')"
                      >
                        <v-icon>
                          file_copy
                        </v-icon>
                      </v-btn>
                    </template>
                    Generate a document
                  </v-tooltip>
                  <v-tooltip
                    v-if="$can('edit', 'Yacht')"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        class="mr-2"
                        :to="{ name: 'yachtComments', params: { id: yacht.id }}"
                        v-on="on"
                      >
                        <v-icon>
                          comment
                        </v-icon>
                      </v-btn>
                    </template>
                    Add a comment / note
                  </v-tooltip>
                  <v-chip
                    v-if="$can('view-sale-price', 'Yacht') && yacht.total_sale_price && yacht.currency"
                    color="primary"
                    class="mr-2"
                    :class="{'clickable': $can('set-finance-model', 'Yacht')}"
                    dark
                    @click="$emit('edit:pricing')"
                  >
                    <strong>
                      {{ yacht.total_sale_price | currency(yacht.currency) }}
                    </strong>
                  </v-chip>

                  <v-chip
                    v-if="$can('view-sale-price', 'Yacht') && savedEurUsdRate"
                    color="grey darken-2"
                    outlined
                    class="mr-2"
                  >
                    EUR/USD: {{ savedEurUsdRate.rate }}
                  </v-chip>

                  <v-chip
                    v-if="$can('view-sale-price', 'Yacht') && yacht.finance_model"
                    color="grey darken-2"
                    class="mr-2"
                    outlined
                    @click="$emit('edit:pricing')"
                  >
                    @ {{ yacht.finance_model.label }}
                  </v-chip>

                  <v-chip
                    v-if="$can('view-sale-price', 'Yacht') && Number(yacht.total_sale_price_final) !== Number(yacht.total_sale_price)"
                    color="red"
                    outlined
                    @click="$emit('edit:pricing')"
                  >
                    {{ yacht.total_sale_price_final / yacht.total_sale_price - 1 | percentage(2) }}
                    =
                    {{ yacht.total_sale_price_final | currency(yacht.currency) }}
                  </v-chip>
                </v-col>
              </v-row>
              <div class="col col-auto">
                <navigation :yacht="yacht" />
              </div>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-app-bar
      fixed
      :value="showToolbar"
      dense
      clipped-left
      style="margin-left: 330px"
    >
      <navigation :yacht="yacht" />
    </v-app-bar>
  </div>
</template>
<script>
import Yacht from '@/models/Yacht'
import Navigation from '@/components/business/yachts/details/YachtDetailsNavigation'

export default {
    name: 'YachtDetailsHeader',
    components: { Navigation },
    props: {
      yacht: {
        type: Object,
        required: true
      },
      documentTemplates: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        yachtStatuses: Yacht,
        showToolbar: false
      }
    },
    computed: {
      canEdit() {
        return this.yacht.status === this.yachtStatuses.STATUS_DRAFT || this.yacht.status === this.yachtStatuses.STATUS_SALES
      },
      savedEurUsdRate() {
        if (!this.yacht.id) return undefined

        const rates = []

        if (this.yacht.currency_exchange_rate && parseFloat(this.yacht.currency_exchange_rate) !== 1) {
          rates.push({
            base: this.yacht.original_currency,
            quote: this.yacht.currency,
            rate: this.yacht.currency_exchange_rate
          })
        }

        this.yacht.equipment_packs.map(ep => {
          if (!ep.currency_exchange_rate) return

          rates.push({
            base: ep.currency,
            quote: ep.final_currency,
            rate: ep.currency_exchange_rate
          })
        })

        const eurUsdRate = rates.find(r => (r.base === 'EUR' && r.quote === 'USD') || (r.quote === 'EUR' && r.base === 'USD'))

        if (!eurUsdRate) return undefined

        if (eurUsdRate.base === 'EUR') return eurUsdRate

        return {
          'base': 'EUR',
          'quote': 'USD',
          'rate': (1 / eurUsdRate.rate).toFixed(6)
        }
      }
    },
    methods: {
      openDocumentCreator(templateId) {
        this.$emit('document:preview', templateId)
      },
      onIntersect(entries, observer) {
          this.showToolbar = entries[0].intersectionRatio <= 0.0
      }
    }
  }
</script>
